import {
  Image,
  Text,
  Box,
  useToken,
  Button,
  HStack,
  VStack,
  Divider,
  Grid,
  Flex,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { interpretBoldTags } from "../../../utils/processPaywallText";
import { RadioOffIcon } from "../../icons/RadioOffIcon";
import { RadioOnIcon } from "../../icons/RadioOnIcon";
import { PaywallYearlyBundleType, SubscriptionOfferV2Type } from "../types";

const MotionBox = motion(Box);
export function SubscriptionSelector({
  isActive,
  onClick,
  creatorLogos,
  creatorsCount,
  subOffer,
  yearlyBundle,
  alwaysExpanded = false,
  hideDiscountEyebrow = false,
}: {
  isActive: boolean;
  onClick: () => void;
  creatorLogos?: string[];
  creatorsCount?: number;
  subOffer: SubscriptionOfferV2Type;
  yearlyBundle?: PaywallYearlyBundleType;
  /**
   * When true, the yearly-bundle and creator-logo panels render without
   * the expand/collapse animation and stay visible regardless of active
   * state. Used on pages that don't tolerate selector size changes.
   */
  alwaysExpanded?: boolean;
  /**
   * When true, the discount-string pill that normally sits above the
   * card (e.g. "50% OFF") is suppressed. The sale is still surfaced
   * via the strikethrough original_price_string inside the card.
   */
  hideDiscountEyebrow?: boolean;
}) {
  const [resolvedWhite, resolvedBlaze] = useToken("colors", [
    "white",
    "blaze.blaze",
  ]);
  const { tier_name, sub_text, price_string } = subOffer;
  const priceStringText = interpretBoldTags(
    price_string,
    700,
    "inherit",
    resolvedWhite,
  );
  const isYearly = subOffer.billing_frequency === "yearly";
  const billedText = isYearly
    ? "Billed yearly. Cancel anytime."
    : "Billed monthly. Cancel anytime.";
  const hasGift =
    isYearly && !!subOffer.welcome_gifts?.length && !!yearlyBundle;
  const giftDescriptionHtml = hasGift
    ? interpretBoldTags(yearlyBundle.description, 400, "inherit", resolvedBlaze)
    : "";
  return (
    <VStack width="100%" alignItems="end" gap={0}>
      {hasGift ? (
        <Box
          borderTopRadius="8px"
          p="4px 16px"
          marginBottom={isActive ? "-2px" : "-1px"}
          background="linear-gradient(90deg, #FF5A2C 0%, #EF44BF 100%)"
          boxShadow="0 10px 15px -3px rgba(0, 0, 0, 0.10)"
        >
          <Text variant="paymentDiscountString" textTransform="uppercase">
            {yearlyBundle.eyebrow}
          </Text>
        </Box>
      ) : !hideDiscountEyebrow && subOffer.discount_string ? (
        <Box
          borderTopRadius="8px"
          p="4px 16px"
          marginBottom={isActive ? "-2px" : "-1px"}
          background="linear-gradient(90deg, #FF5A2C 0%, #EF44BF 100%)"
          boxShadow="0 10px 15px -3px rgba(0, 0, 0, 0.10)"
        >
          <Text variant="paymentDiscountString">
            {subOffer.discount_string}
          </Text>
        </Box>
      ) : null}
      <Button
        gc-testing-id={
          subOffer.billing_frequency === "monthly" ? "btnMonthly" : "btnYearly"
        }
        flexDirection="column"
        w="full"
        height="auto"
        position="relative"
        isolation="isolate"
        onClick={() => onClick()}
        border="none"
        _active={{}}
        p={4}
        background="transparent.black.30"
        borderRadius="16px"
        borderTopRightRadius={
          hasGift || (!hideDiscountEyebrow && subOffer.discount_string)
            ? "0px"
            : "16px"
        }
        gap={3}
        whiteSpace="normal"
        _before={{
          content: '""',
          position: "absolute",
          inset: 0,
          zIndex: -1,
          borderRadius: "inherit",
          padding: isActive ? "2px" : "1px",
          background: isActive
            ? "linear-gradient(45deg, #FF5A2C 0%, #FF5A2C66 50%, #FF5A2C 100%)"
            : "linear-gradient(45deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0.1) 50%, rgba(255, 255, 255, 0.3) 100%)",
          WebkitMask:
            "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
          WebkitMaskComposite: "xor",
          maskComposite: "exclude",
          pointerEvents: "none",
          transition: "padding 0.2s ease-in-out, background 0.2s ease-in-out",
        }}
      >
        <HStack align="center" justifyContent="space-between" width="100%">
          <HStack gap={3} alignItems="center">
            {isActive ? (
              <RadioOnIcon
                boxSize={6}
                color="blaze.blaze"
                stroke="blaze.blaze"
                strokeWidth="1px"
              />
            ) : (
              <RadioOffIcon
                boxSize={6}
                color="dune.600"
                stroke="dune.600"
                strokeWidth="1px"
              />
            )}
            <VStack alignItems="flex-start">
              <Text variant="paymentPriceTier">{tier_name}</Text>
              {sub_text ? (
                <HStack gap={1}>
                  {sub_text.icon_url ? (
                    <Image
                      src={sub_text.icon_url}
                      alt={sub_text.text}
                      w="10.5px"
                      h={3}
                    />
                  ) : null}
                  <Text variant="paymentSubText" color={sub_text.color}>
                    {sub_text.text}
                  </Text>
                </HStack>
              ) : null}
            </VStack>
          </HStack>
          <VStack alignItems="flex-end" gap={0}>
            <Text
              variant="paymentPriceString"
              dangerouslySetInnerHTML={{ __html: priceStringText }}
              whiteSpace="normal"
              textAlign="right"
            />
            {subOffer.original_price_string &&
            (subOffer.has_promo_discount || subOffer.is_intro_pricing) ? (
              <Text
                color="blaze.600"
                fontSize="13px"
                fontWeight="700"
                letterSpacing=".5px"
                lineHeight="140%"
                textDecoration="line-through"
              >
                {subOffer.original_price_string}
              </Text>
            ) : null}
          </VStack>
        </HStack>
        {hasGift && (
          <MotionBox
            initial={false}
            animate={
              alwaysExpanded
                ? { height: "auto", opacity: 1, marginTop: 0 }
                : {
                    height: isActive ? "auto" : 0,
                    opacity: isActive ? 1 : 0,
                    marginTop: isActive ? 0 : -12,
                  }
            }
            transition={{
              duration: alwaysExpanded ? 0 : 0.2,
              ease: "easeInOut",
            }}
            overflow="hidden"
            width="100%"
          >
            <VStack gap={4} width="100%">
              <Divider borderColor="transparent.white.10" />
              <HStack
                gap={2}
                width="100%"
                alignItems="center"
                borderRadius="8px"
              >
                <VStack alignItems="flex-start" gap="2px" flex={1} minW={0}>
                  <Text
                    fontSize={{ base: "13px", md: "16px" }}
                    lineHeight={{ base: "1.5", md: "1.6" }}
                    color="white"
                    textAlign="left"
                    width="100%"
                    dangerouslySetInnerHTML={{ __html: giftDescriptionHtml }}
                  />
                  <Text
                    fontSize="12px"
                    lineHeight="1.5"
                    color="neutral.400"
                    textAlign="left"
                    width="100%"
                  >
                    {yearlyBundle.subtitle}
                  </Text>
                </VStack>
                {yearlyBundle.thumbnail_url && (
                  <Image
                    src={yearlyBundle.thumbnail_url}
                    alt={yearlyBundle.slug}
                    width="150px"
                    height="auto"
                    objectFit="contain"
                    flexShrink={0}
                    ml="24px"
                  />
                )}
              </HStack>
            </VStack>
          </MotionBox>
        )}
        {creatorLogos ? (
          <MotionBox
            initial={false}
            animate={
              alwaysExpanded
                ? { height: "auto", opacity: 1, marginTop: 0 }
                : {
                    height: isActive ? "auto" : 0,
                    opacity: isActive ? 1 : 0,
                    marginTop: isActive ? 0 : -12,
                  }
            }
            transition={{
              duration: alwaysExpanded ? 0 : 0.2,
              ease: "easeInOut",
            }}
            overflow="hidden"
            width="100%"
          >
            <VStack gap={3} minWidth={0} width="100%">
              <Divider colorScheme="transparent.white.10" />
              <Text variant="paymentSelector" fontWeight="500">
                Supported by All Major Publishers
              </Text>
              <Grid
                templateColumns="repeat(5, 1fr)"
                w="100%"
                gap={{ base: 1, md: 2 }}
                justifyContent="center"
                minWidth={0}
              >
                {creatorLogos.map((logo, i) => (
                  <Flex
                    alignItems="center"
                    justifyContent="center"
                    key={i}
                    background="black"
                    borderRadius="7px"
                    aspectRatio={1}
                    p={{ base: 1.5, md: 3 }}
                  >
                    <Image src={logo} alt={logo} boxSize="100%" />
                  </Flex>
                ))}
                <Flex
                  alignItems="center"
                  justifyContent="center"
                  background="black"
                  borderRadius="7px"
                  aspectRatio={1}
                  p={{ base: 1.5, md: 3 }}
                >
                  <Text variant="paymentSelector" lineHeight="110%">
                    +{creatorsCount}
                    <br />
                    more
                  </Text>
                </Flex>
              </Grid>
            </VStack>
          </MotionBox>
        ) : null}
        <Divider borderColor="transparent.white.10" />
        <Text variant="paymentSelector" textAlign="left" width="100%">
          {billedText}
        </Text>
      </Button>
    </VStack>
  );
}
